const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

/**
 * TZN checkout safeguard
 * Enforces the 19 EUR minimum order value on every storefront checkout entry point.
 * The customer-facing minimum-order message is rendered by the existing cart UI;
 * this safeguard only blocks checkout and accelerated checkout bypasses.
 */
(() => {
  const MINIMUM_ORDER_VALUE = 1900;
  const CART_CHECKOUT_BUTTONS = '#checkout, #CartDrawer-Checkout';
  const CART_EXPRESS_CHECKOUT = '.additional-checkout-buttons';
  const PDP_EXPRESS_CHECKOUT = '.shopify-payment-button, [data-shopify="payment-button"]';

  let minimumOrderBlocked = null;
  let syncTimer;
  let syncing = false;

  function setElementHidden(element, hidden, marker) {
    if (!element) return;

    if (hidden) {
      if (!element.hasAttribute(marker)) {
        element.setAttribute(marker, 'true');
      }
      element.style.display = 'none';
      element.setAttribute('aria-hidden', 'true');
    } else if (element.getAttribute(marker) === 'true') {
      element.style.removeProperty('display');
      element.removeAttribute('aria-hidden');
      element.removeAttribute(marker);
    }
  }

  function disablePdpExpressCheckout() {
    document.querySelectorAll(PDP_EXPRESS_CHECKOUT).forEach((element) => {
      setElementHidden(element, true, 'data-tzn-pdp-express-disabled');
    });
  }

  function removeDuplicateMinimumOrderMessage() {
    document.querySelectorAll('.tzn-minimum-order-message').forEach((message) => message.remove());
  }

  function applyMinimumOrderState(cart) {
    const itemCount = Number(cart?.item_count || 0);
    const totalPrice = Number(cart?.total_price || 0);
    const isBlocked = itemCount > 0 && totalPrice < MINIMUM_ORDER_VALUE;

    minimumOrderBlocked = isBlocked;
    removeDuplicateMinimumOrderMessage();

    document.querySelectorAll(CART_CHECKOUT_BUTTONS).forEach((button) => {
      button.disabled = itemCount === 0 || isBlocked;

      if (isBlocked) {
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('data-tzn-minimum-order-blocked', 'true');
      } else {
        button.removeAttribute('data-tzn-minimum-order-blocked');
        if (itemCount > 0) button.removeAttribute('aria-disabled');
      }
    });

    document.querySelectorAll(CART_EXPRESS_CHECKOUT).forEach((container) => {
      setElementHidden(container, isBlocked, 'data-tzn-minimum-order-hidden');
    });

    disablePdpExpressCheckout();
  }

  async function syncMinimumOrderGuard() {
    if (syncing) return;
    syncing = true;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error(`Cart request failed with status ${response.status}`);
      const cart = await response.json();
      applyMinimumOrderState(cart);
    } catch (error) {
      minimumOrderBlocked = true;
      removeDuplicateMinimumOrderMessage();
      document.querySelectorAll(CART_CHECKOUT_BUTTONS).forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('data-tzn-minimum-order-blocked', 'true');
      });
      document.querySelectorAll(CART_EXPRESS_CHECKOUT).forEach((container) => {
        setElementHidden(container, true, 'data-tzn-minimum-order-hidden');
      });
      disablePdpExpressCheckout();
      console.warn('TZN checkout safeguard could not refresh the cart state.', error);
    } finally {
      syncing = false;
    }
  }

  function scheduleMinimumOrderSync(delay = 80) {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncMinimumOrderGuard, delay);
  }

  function shouldBlockCheckoutInteraction(target) {
    if (!target) return false;

    const cartCheckout = target.closest?.(
      `${CART_CHECKOUT_BUTTONS}, ${CART_EXPRESS_CHECKOUT}, a[href*="/checkout"]`
    );
    if (cartCheckout && minimumOrderBlocked !== false) return true;

    const pdpExpress = target.closest?.(PDP_EXPRESS_CHECKOUT);
    return Boolean(pdpExpress);
  }

  document.addEventListener(
    'click',
    (event) => {
      if (!shouldBlockCheckoutInteraction(event.target)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      scheduleMinimumOrderSync(0);
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      const isCartForm = form?.id === 'cart' || form?.id === 'CartDrawer-Form';
      const isCheckoutSubmit = event.submitter?.name === 'checkout';

      if (isCartForm && isCheckoutSubmit && minimumOrderBlocked !== false) {
        event.preventDefault();
        event.stopImmediatePropagation();
        scheduleMinimumOrderSync(0);
      }
    },
    true
  );

  if (document.body) {
    const observer = new MutationObserver(() => {
      removeDuplicateMinimumOrderMessage();
      disablePdpExpressCheckout();
      scheduleMinimumOrderSync();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        removeDuplicateMinimumOrderMessage();
        disablePdpExpressCheckout();
        scheduleMinimumOrderSync(0);
      },
      { once: true }
    );
  } else {
    removeDuplicateMinimumOrderMessage();
    disablePdpExpressCheckout();
    scheduleMinimumOrderSync(0);
  }

  window.addEventListener('pageshow', () => scheduleMinimumOrderSync(0));
})();
